import cx from 'classnames';
import React, { FC, useState } from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { useRouter } from 'next/router';
// eslint-disable-next-line no-restricted-imports
import ThreadsButton from 'plaid-threads/Button';
import { Events } from 'src/lib/trackingEvents';

// contexts
import { usePageLocale } from 'src/contexts';
import { useTrack } from 'src/contexts/AnalyticsContext';

// helpers
import { prependLocale, prependStagingHash } from 'src/lib/utils';

// styles
import styles from './Button.module.scss';

interface Classes {
  divClassName?: string;
  aClassName?: string;
  buttonClassName?: string;
}
interface Props {
  type?: 'submit' | 'reset' | 'button';
  value?: string;
  form?: string;
  href?: string;
  invertedColors?: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  primary?: boolean;
  secondary?: boolean;
  fullWidth?: boolean;
  inline?: boolean;
  noMarginBottom?: boolean;
  noMinWidth?: boolean;
  'data-id'?: string;
  small?: boolean;
  centered?: boolean;
  classes?: Classes;
  onClick?: (event) => void;
  trackingId?: string;
  noHref?: boolean;
  target?: string;
  showLoader?: boolean;
  submitting?: boolean;
  animatedCaret?: boolean;
  entryId?: string;
}

const COLOR_ARRAY = ['blue', 'green', 'pink', 'purple'];

const Button: FC<Props> = ({
  invertedColors = false,
  children,
  className = '', // NOTE: this is now deprecated in favor of classes.aClassName
  disabled = false,
  secondary = false,
  fullWidth = false,
  noMarginBottom = false,
  noMinWidth = false,
  inline = !fullWidth,
  onClick = () => {
    return undefined;
  },
  trackingId = '',
  href = '',
  noHref = false,
  type = 'button',
  animatedCaret = true,
  entryId,
  ...props
}) => {
  const inspectorProps = useContentfulInspectorMode({ entryId });
  const track = useTrack();
  const router = useRouter();
  const userLocale = usePageLocale();
  const locale = router?.locale || userLocale;
  const basePath = router?.basePath || '';

  const [color, setColor] = useState(null);
  const colorArray = COLOR_ARRAY;
  const [remainingColors, setRemainingColors] = useState(colorArray);

  const changeButtonColor = () => {
    if (remainingColors.length > 0) {
      const randomIndex = Math.floor(Math.random() * remainingColors.length);
      setColor(remainingColors[randomIndex]);
      setRemainingColors(
        remainingColors.filter((color, idx) => {
          return idx !== randomIndex;
        }),
      );
    } else {
      const randomIndex = Math.floor(Math.random() * colorArray.length);
      setColor(colorArray[randomIndex]);
      setRemainingColors(
        colorArray.filter((color, idx) => {
          return idx !== randomIndex;
        }),
      );
    }
  };

  const clearButtonColor = () => {
    return setColor(null);
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    track({
      type: Events.BUTTON_CLICKED,
      payload: { trackingId, event, currentTarget: event.currentTarget },
    });
    onClick(event);
  };

  const handleHref = () => {
    return type === 'submit'
      ? ''
      : prependStagingHash(prependLocale({ locale, href }), basePath);
  };

  return (
    <div
      onMouseEnter={() => {
        !disabled && changeButtonColor();
      }}
      onMouseLeave={() => {
        return clearButtonColor();
      }}
      className={cx(
        styles.wrapper,
        'button-module-wrapper',
        secondary ? styles.outlinedButtonWrapper : styles.filledButtonWrapper,
        {
          [styles.invertedColorButtonWrapper]: invertedColors,
          [styles.fullWidthButtonWrapper]: fullWidth,
        },
        props?.classes?.divClassName,
        props?.classes?.buttonClassName,
      )}
      data-id={props['data-id']}
      {...(entryId ? inspectorProps({ fieldId: 'text' }) : null)}
    >
      <ThreadsButton
        className={cx(
          styles[color],
          styles.threadsButton,
          noMarginBottom && styles.noMarginBottom,
          noMinWidth && styles.noMinWidthButton,
          !props.small && 'button',
          props?.classes?.aClassName || className,
        )}
        secondary={secondary}
        disabled={disabled}
        inline={inline}
        onClick={handleClick}
        animatedCaret={animatedCaret}
        type={type}
        href={noHref === true ? undefined : handleHref()}
        {...props}
      >
        {children}
      </ThreadsButton>
    </div>
  );
};

export default Button;
